import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import type { PublicShopSkinDto } from '../api/api.client';
import { shopPublicPath } from '../domain/shop-theme';

export interface AppliedShopSkin extends PublicShopSkinDto {
  readonly farmName: string;
  readonly homePath: string;
}

/**
 * Peau de la boutique directe `/chez/:slug`.
 *
 * Surcharge les jetons `--ct-*` sur `:root` le temps de la visite, puis les
 * retire en quittant. Le marché `/producteur/:id` n'y passe pas.
 */
@Injectable({ providedIn: 'root' })
export class ShopSkinService {
  private readonly document = inject(DOCUMENT);
  private appliedKeys: string[] = [];

  readonly skin = signal<AppliedShopSkin | null>(null);

  enter(farmName: string, shop: PublicShopSkinDto): void {
    this.clearVars();
    const root = this.document.documentElement;
    const keys: string[] = [];
    for (const [key, value] of Object.entries(shop.cssVars)) {
      root.style.setProperty(key, value);
      keys.push(key);
    }
    this.appliedKeys = keys;
    root.classList.add('ct-shop-skin');
    root.classList.toggle('ct-shop-chrome', shop.chrome);
    root.classList.toggle('ct-shop-white-label', shop.whiteLabel);
    root.classList.toggle('ct-shop-motion-vif', shop.motion === 'vif');
    root.classList.toggle('ct-shop-motion-soft', shop.motion === 'soft');
    this.skin.set({
      ...shop,
      farmName,
      homePath: shopPublicPath(shop.slug),
    });
  }

  clear(): void {
    this.clearVars();
    const root = this.document.documentElement;
    root.classList.remove(
      'ct-shop-skin',
      'ct-shop-chrome',
      'ct-shop-white-label',
      'ct-shop-motion-vif',
      'ct-shop-motion-soft',
    );
    this.skin.set(null);
  }

  private clearVars(): void {
    const root = this.document.documentElement;
    for (const key of this.appliedKeys) {
      root.style.removeProperty(key);
    }
    this.appliedKeys = [];
  }
}
